import { useContext, Dispatch, SetStateAction, useCallback, useMemo } from 'react';
import { MutationTuple, QueryResult } from '@apollo/client/react/types/types';
import { useMutation, useQuery, MutationUpdaterFn, gql, Reference, ApolloError } from '@apollo/client';
import { IDocumentsNode, DocsNodeType, IApiFolderNode, IApiDocumentsNode, IApiFileNode } from './types';
import {
  LOAD_FILES,
  REMOVE_FOLDER,
  REMOVE_FILE,
  UPDATE_FOLDER,
  UPDATE_FILE,
  ATTACH_FILE,
  DELETE_ATTACHED_FILE,
  CREATE_FOLDER,
  UPLOAD_FILE
} from './queries';
import {
  NavFoldersContext,
  INavFolder,
  UpdateNavFoldersContext,
  UpdateCurrentFolderContext,
  CurrentFolderContext,
  ActiveDocNodeContext,
  UpdateActiveDocNodeContext
} from 'src/common/DocumentsLibrary/context';
import { IApiChecklistItem } from '../bids/BidChecklist/types';
import { RcFile } from 'antd/lib/upload';
import { useTranslation } from 'react-i18next';
import notification from 'src/common/notification';
import { User } from '../users';
import { FileStatus } from 'src/common/DocumentsLibrary/types';
import { IApiUser } from '../users/types';

const nodeFragment = gql`
  fragment NodeFragment on DirectoryNode {
    id
  }
`;

export function useNavFoldersContext(): INavFolder[] {
  return useContext(NavFoldersContext);
}

export function useUpdateNavFoldersContext(): Dispatch<SetStateAction<INavFolder[]>> | null {
  return useContext(UpdateNavFoldersContext);
}

export function useCurrentFolderContext(): INavFolder {
  return useContext(CurrentFolderContext);
}

export function useUpdateCurrentFolderContext(): Dispatch<SetStateAction<INavFolder>> | null {
  return useContext(UpdateCurrentFolderContext);
}

export function useActiveDocNode(): [string | null, Dispatch<SetStateAction<string | null>> | null] {
  const activeDocNode = useContext(ActiveDocNodeContext);
  const updateActiveDocNode = useContext(UpdateActiveDocNodeContext);

  return [activeDocNode, updateActiveDocNode];
}

export function useLoadDocuments(
  id: string | null
): Omit<QueryResult<{ ls: IApiDocumentsNode[] }, { parentId: string | null }>, 'data'> & { data?: IDocumentsNode[] } {
  const query = useQuery<{ ls: IApiDocumentsNode[] }, { parentId: string | null }>(LOAD_FILES, {
    variables: { parentId: id }
  });

  return useMemo(
    () => ({
      ...query,
      data: query.data
        ? query.data.ls.map(node => ({ ...node, createdBy: node.createdBy ? new User(node.createdBy) : null }))
        : undefined
    }),
    [query]
  );
}

export function useDeleteFile(): MutationTuple<{ rmFile: boolean; __typename: 'Mutation' }, { id: string }> {
  return useMutation(REMOVE_FILE);
}

export function getUpdateCacheOnDeleteFile(
  id: string,
  parentId: string | null
): MutationUpdaterFn<{ rmFile: boolean; __typename: 'Mutation' }> {
  return (cache, { data }) => {
    if (!data) {
      return;
    }
    if (parentId) {
      const parentRef = cache.identify({
        __typename: 'DirectoryNode',
        id: parentId
      });
      cache.modify({
        id: parentRef,
        fields: {
          children(existingChildrenRefs: Reference[], { readField }) {
            return existingChildrenRefs.filter(childrenRef => id !== readField('id', childrenRef));
          }
        }
      });
    }
    cache.evict({ id: cache.identify({ id, __typename: 'FileNode' }) });

    const lsQueryData = cache.readQuery<{ ls: IApiDocumentsNode[] } | null>({
      query: LOAD_FILES,
      variables: { parentId }
    });
    if (!lsQueryData) {
      return;
    }
    const { ls } = lsQueryData;
    cache.writeQuery({
      query: LOAD_FILES,
      variables: { parentId },
      data: { ls: ls.filter(file => file.id !== id) }
    });
  };
}

export function useDeleteFolder(): MutationTuple<{ rmFolder: boolean; __typename: 'Mutation' }, { id: string }> {
  return useMutation(REMOVE_FOLDER);
}

export function getUpdateCacheOnDeleteFolder(
  id: string,
  parentId: string | null
): MutationUpdaterFn<{ rmFolder: boolean; __typename: 'Mutation' }> {
  return (cache, { data }) => {
    if (!data) {
      return;
    }
    if (parentId) {
      const parentRef = cache.identify({
        __typename: 'DirectoryNode',
        id: parentId
      });
      cache.modify({
        id: parentRef,
        fields: {
          children(existingChildrenRefs: Reference[], { readField }) {
            return existingChildrenRefs.filter(childrenRef => id !== readField('id', childrenRef));
          }
        }
      });
    }
    cache.evict({ id: cache.identify({ id, __typename: 'DirectoryNode' }) });

    const lsQueryData = cache.readQuery<{ ls: IApiDocumentsNode[] } | null>({
      query: LOAD_FILES,
      variables: { parentId }
    });
    if (!lsQueryData) {
      return;
    }
    const { ls } = lsQueryData;
    cache.writeQuery({
      query: LOAD_FILES,
      variables: { parentId },
      data: { ls: ls.filter(folder => folder.id !== id) }
    });
  };
}

export interface IUpdateFolderResponse {
  __typename: 'Mutation';
  updateFolder: IApiFolderNode & {
    __typename: 'DirectoryNode';
    createdBy:
      | (IApiUser & {
          __typename: 'User';
        })
      | null;
  };
}
export function useUpdateFolder(): MutationTuple<
  IUpdateFolderResponse,
  { id: string; parentId?: string | null; name: string }
> {
  return useMutation(UPDATE_FOLDER);
}

export function getUpdateCacheOnFolderMove(
  itemId: string,
  fromId: string | null,
  toId: string | null
): MutationUpdaterFn<IUpdateFolderResponse> {
  return (cache, { data }) => {
    if (!data) {
      return;
    }
    const lsQueryDataFrom = cache.readQuery<{ ls: IApiDocumentsNode[] } | null>({
      query: LOAD_FILES,
      variables: { parentId: fromId }
    });
    const lsQueryDataTo = cache.readQuery<{ ls: IApiDocumentsNode[] } | null>({
      query: LOAD_FILES,
      variables: { parentId: toId }
    });
    if (!lsQueryDataFrom) {
      return;
    }
    const { ls: lsFrom } = lsQueryDataFrom;

    cache.writeQuery({
      query: LOAD_FILES,
      variables: { parentId: fromId },
      data: { ls: lsFrom.filter(folder => folder.id !== itemId) }
    });
    if (lsQueryDataTo) {
      const { ls: lsTo } = lsQueryDataTo;
      const lsToFolderLastIndex = lsTo.map(doc => doc.nodeType).lastIndexOf(DocsNodeType.FOLDER);
      const newLsTo =
        lsToFolderLastIndex === -1
          ? [data.updateFolder, ...lsTo]
          : [...lsTo.slice(0, lsToFolderLastIndex + 1), data.updateFolder, ...lsTo.slice(lsToFolderLastIndex + 1)];
      cache.writeQuery({
        query: LOAD_FILES,
        variables: { parentId: toId },
        data: { ls: newLsTo }
      });
    }
    const movedItemRef = cache.writeFragment({
      data: { id: itemId, __typename: 'DirectoryNode' },
      fragment: nodeFragment,
      fragmentName: 'NodeFragment'
    });
    if (fromId) {
      const fromRef = cache.identify({
        __typename: 'DirectoryNode',
        id: fromId
      });
      cache.modify({
        id: fromRef,
        fields: {
          children(existingChildrenRefs: Reference[], { readField }) {
            const newChildren = existingChildrenRefs.filter(childrenRef => itemId !== readField('id', childrenRef));
            return newChildren;
          }
        }
      });
    }
    if (toId) {
      const toRef = cache.identify({
        __typename: 'DirectoryNode',
        id: toId
      });
      cache.modify({
        id: toRef,
        fields: {
          children(existingChildrenRefs: Reference[], { readField }) {
            const newChildren = existingChildrenRefs.some(ref => readField('id', ref) === itemId)
              ? existingChildrenRefs
              : [...existingChildrenRefs, movedItemRef];
            return newChildren;
          }
        }
      });
    }
  };
}

export function getUpdateCacheOnFolderRename(
  id: string,
  name: string,
  parentId: string | null
): MutationUpdaterFn<IUpdateFolderResponse> {
  return (cache, { data }) => {
    if (!data) {
      return;
    }
    const lsQueryData = cache.readQuery<{ ls: IApiDocumentsNode[] } | null>({
      query: LOAD_FILES,
      variables: { parentId }
    });
    if (!lsQueryData) {
      return;
    }
    const { ls } = lsQueryData;

    const newFolder = { ...data.updateFolder, name };
    const indexOfUpdatedFolder = ls.findIndex(folder => folder.id === id);
    const newLs = [...ls.slice(0, indexOfUpdatedFolder), newFolder, ...ls.slice(indexOfUpdatedFolder + 1)];
    cache.writeQuery({
      query: LOAD_FILES,
      variables: { parentId },
      data: { ls: newLs }
    });
  };
}

export interface IUpdateFileResponse {
  __typename: 'Mutation';
  updateFile: IApiFileNode & {
    __typename: 'FileNode';
    createdBy:
      | (IApiUser & {
          __typename: 'User';
        })
      | null;
  };
}

export function useUpdateFile(): MutationTuple<
  IUpdateFileResponse,
  { id: string; parentId?: string | null; name: string }
> {
  return useMutation(UPDATE_FILE);
}

export function getUpdateCacheOnFileMove(
  itemId: string,
  fromId: string | null,
  toId: string | null
): MutationUpdaterFn<IUpdateFileResponse> {
  return (cache, { data }) => {
    if (!data) {
      return;
    }
    const lsQueryDataFrom = cache.readQuery<{ ls: IApiDocumentsNode[] } | null>({
      query: LOAD_FILES,
      variables: { parentId: fromId }
    });
    const lsQueryDataTo = cache.readQuery<{ ls: IApiDocumentsNode[] } | null>({
      query: LOAD_FILES,
      variables: { parentId: toId }
    });
    if (!lsQueryDataFrom) {
      return;
    }
    const { ls: lsFrom } = lsQueryDataFrom;

    cache.writeQuery({
      query: LOAD_FILES,
      variables: { parentId: fromId },
      data: { ls: lsFrom.filter(file => file.id !== itemId) }
    });
    if (lsQueryDataTo) {
      const { ls: lsTo } = lsQueryDataTo;
      if (lsTo.some(file => file.id === itemId)) {
        return;
      }
      cache.writeQuery({
        query: LOAD_FILES,
        variables: { parentId: toId },
        data: { ls: [...lsTo, data.updateFile] }
      });
    }
    const movedItemRef = cache.writeFragment({
      data: { id: itemId, __typename: 'FileNode' },
      fragment: nodeFragment,
      fragmentName: 'NodeFragment'
    });
    if (fromId) {
      const fromRef = cache.identify({
        __typename: 'DirectoryNode',
        id: fromId
      });
      cache.modify({
        id: fromRef,
        fields: {
          children(existingChildrenRefs: Reference[], { readField }) {
            const newChildren = existingChildrenRefs.filter(childrenRef => itemId !== readField('id', childrenRef));
            return newChildren;
          }
        }
      });
    }
    if (toId) {
      const toRef = cache.identify({
        __typename: 'DirectoryNode',
        id: toId
      });
      cache.modify({
        id: toRef,
        fields: {
          children(existingChildrenRefs: Reference[], { readField }) {
            const newChildren = existingChildrenRefs.some(ref => readField('id', ref) === itemId)
              ? existingChildrenRefs
              : [...existingChildrenRefs, movedItemRef];
            return newChildren;
          }
        }
      });
    }
  };
}

export function getUpdateCacheOnFileRename(
  id: string,
  name: string,
  parentId: string | null
): MutationUpdaterFn<IUpdateFileResponse> {
  return (cache, { data }) => {
    if (!data) {
      return;
    }
    const lsQueryData = cache.readQuery<{ ls: IApiDocumentsNode[] } | null>({
      query: LOAD_FILES,
      variables: { parentId }
    });
    if (!lsQueryData) {
      return;
    }
    const { ls } = lsQueryData;

    const newFile = { ...data.updateFile, name };
    const indexOfUpdatedFolder = ls.findIndex(file => file.id === id);
    const newLs = [...ls.slice(0, indexOfUpdatedFolder), newFile, ...ls.slice(indexOfUpdatedFolder + 1)];
    cache.writeQuery({
      query: LOAD_FILES,
      variables: { parentId },
      data: { ls: newLs }
    });
  };
}

export interface ICreateFolderResponse {
  __typename: 'Mutation';
  mkdir: IApiFolderNode & {
    __typename: 'DirectoryNode';
    createdBy: {
      __typename: 'User';
    };
  };
}

export function useCreateFolder(): MutationTuple<ICreateFolderResponse, { parentId: string | null; name: string }> {
  return useMutation(CREATE_FOLDER);
}

export function getUpdateCacheOnFolderCreate(parentId: string | null): MutationUpdaterFn<ICreateFolderResponse> {
  return (cache, { data }) => {
    if (!data) {
      return;
    }
    const lsQueryData = cache.readQuery<{ ls: IApiDocumentsNode[] } | null>({
      query: LOAD_FILES,
      variables: { parentId }
    });
    if (!lsQueryData) {
      return;
    }
    const { ls } = lsQueryData;
    const lsFolderLastIndex = ls.map(doc => doc.nodeType).lastIndexOf(DocsNodeType.FOLDER);
    const newLs =
      lsFolderLastIndex === -1
        ? [data.mkdir, ...ls]
        : [...ls.slice(0, lsFolderLastIndex + 1), data.mkdir, ...ls.slice(lsFolderLastIndex + 1)];
    cache.writeQuery({
      query: LOAD_FILES,
      variables: { parentId },
      data: { ls: newLs }
    });
    if (parentId) {
      const parentRef = cache.identify({
        __typename: 'DirectoryNode',
        id: parentId
      });
      const newFolderRef = cache.writeFragment({
        data: { id: data.mkdir.id, __typename: 'DirectoryNode' },
        fragment: nodeFragment,
        fragmentName: 'NodeFragment'
      });
      cache.modify({
        id: parentRef,
        fields: {
          children(existingChildrenRefs: Reference[] = [], { readField }) {
            const newChildren = existingChildrenRefs.some(ref => readField('id', ref) === data.mkdir.id)
              ? existingChildrenRefs
              : [...existingChildrenRefs, newFolderRef];
            return newChildren;
          }
        }
      });
    }
  };
}

export interface UploadFileResponse {
  uploadFile: IApiFileNode & {
    __typename: 'FileNode';
    createdBy: {
      __typename: 'User';
    };
  };
}
interface UploadFileInput {
  name: string;
  parentId: string | null;
}

const handleBeforeUnload = (event: BeforeUnloadEvent): void => {
  event.preventDefault();
  event.returnValue = 'Upload not finished. Are you sure you want to leave?';
};

export function useUploadFile(): [
  (file: RcFile | File, parentId?: string | null, onComplete?: () => void) => void,
  { loading: boolean; error?: ApolloError }
] {
  const { t } = useTranslation();
  const [uploadFile, { loading, error, client }] = useMutation<UploadFileResponse, UploadFileInput>(UPLOAD_FILE);
  const [, setActiveFile] = useActiveDocNode();

  const uploadFn = useCallback(
    async (file: RcFile | File, parentId?: string | null) => {
      let id = '';
      try {
        if (parentId !== undefined) {
          window.addEventListener('beforeunload', handleBeforeUnload);
          const putUrlData = await uploadFile({
            variables: { name: file.name.normalize(), parentId },
            update: getUpdateCacheOnFileUpload(parentId),
            onCompleted: ({ uploadFile }) => {
              setActiveFile?.(uploadFile.id);
              id = uploadFile.id;
            }
          });
          const url = putUrlData.data?.uploadFile.putUrl;
          if (!url) {
            throw new Error(`Can't get url`);
          }
          const buffer = await file.arrayBuffer();

          const fetchResponse = await fetch(url, {
            method: 'put',
            body: buffer
            // headers: new Headers({
            //   'Content-Type': file.type
            // })
          });

          client.cache.modify({
            id: client.cache.identify({
              __typename: 'FileNode',
              id
            }),
            fields: {
              status() {
                return fetchResponse.ok ? FileStatus.Done : FileStatus.WaitingForUpload;
              }
            }
          });
        }
      } catch (err: unknown) {
        if (err instanceof ApolloError) {
          notification.error({
            description: t('Common.unknownErrorDesc'),
            message: t('Common.unknownError')
          });
        } else {
          console.error(err);
        }
      } finally {
        setActiveFile?.(null);
        window.removeEventListener('beforeunload', handleBeforeUnload);
      }
    },
    [client.cache, setActiveFile, t, uploadFile]
  );

  return [uploadFn, { loading, error }];
}

export function getUpdateCacheOnFileUpload(parentId: string | null): MutationUpdaterFn<UploadFileResponse> {
  return (cache, { data }) => {
    if (!data) {
      return;
    }
    const lsQueryData = cache.readQuery<{ ls: IApiDocumentsNode[] } | null>({
      query: LOAD_FILES,
      variables: { parentId }
    });
    if (lsQueryData) {
      const { ls } = lsQueryData;
      cache.writeQuery({
        query: LOAD_FILES,
        variables: { parentId },
        data: { ls: [...ls, data.uploadFile] }
      });
    }
    const parentRef = cache.identify({
      __typename: 'DirectoryNode',
      id: parentId
    });
    const newFileRef = cache.writeFragment({
      data: { id: data.uploadFile.id, __typename: 'FileNode' },
      fragment: nodeFragment,
      fragmentName: 'NodeFragment'
    });
    cache.modify({
      id: parentRef,
      fields: {
        children(existingChildrenRefs: Reference[] = [], { readField }) {
          const newChildren = existingChildrenRefs.some(ref => readField('id', ref) === data.uploadFile.id)
            ? existingChildrenRefs
            : [...existingChildrenRefs, newFileRef];
          return newChildren;
        }
      }
    });
  };
}

export interface IAttachFileResponse {
  __typename: 'Mutation';
  attachFile: IApiChecklistItem & {
    __typename: 'ChecklistItem';
  };
}

export function useAttachFile(): MutationTuple<IAttachFileResponse, { taskId: string; fileId: string }> {
  return useMutation(ATTACH_FILE);
}

export function getUpdateChecklistItemCacheAfterAttachment(
  checklistItemId: string,
  fileId: string
): MutationUpdaterFn<IAttachFileResponse> {
  return (cache, { data }) => {
    if (!data) {
      return;
    }
    const checklistItemRef = cache.identify({
      __typename: 'ChecklistItem',
      id: checklistItemId
    });
    const fileRef = cache.identify({
      __typename: 'FileNode',
      id: fileId
    });
    const checklistItemFragmentRef = cache.writeFragment({
      data: { id: fileId, __typename: 'ChecklistItem' },
      fragment: nodeFragment,
      fragmentName: 'NodeFragment'
    });
    const fileFragmentRef = cache.writeFragment({
      data: { id: fileId, __typename: 'FileNode' },
      fragment: nodeFragment,
      fragmentName: 'NodeFragment'
    });
    cache.modify({
      id: checklistItemRef,
      fields: {
        attachments(existingAttachmentsRefs: Reference[] = [], { readField }) {
          const newAttachments = existingAttachmentsRefs.some(ref => readField('id', ref) === fileId)
            ? existingAttachmentsRefs
            : [...existingAttachmentsRefs, fileFragmentRef];
          return newAttachments;
        }
      }
    });
    cache.modify({
      id: fileRef,
      fields: {
        attachedTo(existingAttachmentsRefs: Reference[] = [], { readField }) {
          const newAttachments = existingAttachmentsRefs.some(ref => readField('id', ref) === checklistItemId)
            ? existingAttachmentsRefs
            : [...existingAttachmentsRefs, checklistItemFragmentRef];
          return newAttachments;
        }
      }
    });
  };
}

export interface IDeleteAttachedFileResponse {
  __typename: 'Mutation';
  removeAttachment: IApiChecklistItem & {
    __typename: 'ChecklistItem';
  };
}

export function useDeleteAttachedFile(): MutationTuple<
  IDeleteAttachedFileResponse,
  { taskId: string; fileId: string }
> {
  return useMutation(DELETE_ATTACHED_FILE);
}

export function getUpdateChecklistItemCacheAfterDeleteAttachment(
  checklistItemId: string,
  fileId: string
): MutationUpdaterFn<IDeleteAttachedFileResponse> {
  return (cache, { data }) => {
    if (!data) {
      return;
    }
    const checklistItemRef = cache.identify({
      __typename: 'ChecklistItem',
      id: checklistItemId
    });
    const fileRef = cache.identify({
      __typename: 'FileNode',
      id: fileId
    });
    cache.modify({
      id: checklistItemRef,
      fields: {
        attachments(existingAttachmentsRefs: Reference[], { readField }) {
          const newAttachments = existingAttachmentsRefs.filter(ref => fileId !== readField('id', ref));
          return newAttachments;
        }
      }
    });
    cache.modify({
      id: fileRef,
      fields: {
        attachedTo(existingChecklistItemsRefs: Reference[], { readField }) {
          const newAttachments = existingChecklistItemsRefs.filter(ref => checklistItemId !== readField('id', ref));
          return newAttachments;
        }
      }
    });
  };
}
